import classnames from 'classnames'
import { type CSSProperties } from 'react'
import { type IBadgeVariant, type IIconsVector, type IStatusProps } from 'shared/ui'
import { Icon, type IIconProps } from 'shared/ui/Icon'
import { Badge, type IBadgeProps } from 'shared/ui/Badge'
import { type ITooltipProps, Tooltip } from 'shared/ui/Tooltip'
import { NavLink } from 'shared/ui/NavLink'
import { type TextStylesType, Typography } from 'shared/ui/Typography'

import { Status } from 'shared/ui/Status'
import { getAriaLabel } from 'shared/lib'
import styles from './styles.module.scss'

export type ITabItem<T = string> = {
  key: T
  name: string
  count?: number
  disabled?: boolean
  icon?: IIconsVector
  iconProps?: IIconProps
  badgeProps?: IBadgeProps
  tooltipProps?: ITooltipProps
  statusProps?: IStatusProps
}

type ITabsProps<T = string> = {
  variant?:
    | 'default'
    | 'simple'
    | 'divider'
    | 'simpleWithoutBorder'
    | 'dividerForFirst'
    | 'circular'
  size?: 'default' | 'large'
  tabs: ITabItem<T>[]
  activeTabKey?: T
  handleSelectTab?: (tab: ITabItem<T>) => void
  handleClickTab?: () => void
  marginInlineTab?: number
  margin?: CSSProperties['margin']
  paddingInlineWrap?: number
  withStopPropagation?: boolean
  gap?: CSSProperties['gap']
  tabHeight?: CSSProperties['height']
  isFullWidth?: boolean
  disabledHover?: boolean
  tabsClassName?: string
  tabClassName?: string
  countVariant?: IBadgeVariant
  badgeActive?: boolean
  isNavigate?: boolean
  isDefaultOverflow?: boolean
}

export const Tabs = <T,>(props: ITabsProps<T>) => {
  const {
    isNavigate,
    tabs,
    activeTabKey,
    handleSelectTab,
    handleClickTab,
    marginInlineTab,
    paddingInlineWrap,
    withStopPropagation,
    variant,
    gap,
    tabHeight,
    isFullWidth,
    margin,
    disabledHover,
    tabsClassName,
    tabClassName,
    size = 'default',
    badgeActive,
    isDefaultOverflow,
  } = props

  return (
    <div
      className={classnames(
        styles.tabs,
        styles[`tabs--${variant}`],
        styles[`tabs--size_${size}`],
        { [styles.defaultOverflow]: isDefaultOverflow },
        tabsClassName
      )}
      style={{ marginInline: marginInlineTab, margin }}
      aria-label={getAriaLabel('Tabs')}
    >
      <div
        className={classnames(styles.wrap, { [styles.fullWidth]: isFullWidth })}
        style={{ paddingInline: paddingInlineWrap, gap }}
        aria-label={getAriaLabel('Tabs', 'wrap')}
      >
        {tabs.map((tab) => {
          const classes = (isActive: boolean) =>
            classnames(styles.tab, tabClassName, {
              [styles.active]: isActive,
              [styles.disabled]: tab.disabled,
              [styles.disabledHover]: disabledHover,
              [styles.badgeActive]: badgeActive,
            })

          return isNavigate ? (
            <NavLink
              key={String(tab.key)}
              to={String(tab.key)}
              className={({ isActive }) => {
                return classes(isActive)
              }}
              style={{
                height: tabHeight,
              }}
              aria-label={getAriaLabel('tab', tab.name)}
            >
              <TabContent {...props} item={tab} isActive={tab.key === activeTabKey} />
            </NavLink>
          ) : (
            <div
              key={String(tab.key)}
              className={classes(tab.key === activeTabKey)}
              onClick={(e) => {
                withStopPropagation && e.stopPropagation()
                handleClickTab?.()

                if (tab.key === activeTabKey) return
                if (tab.disabled) return

                handleSelectTab?.(tab)
              }}
              style={{
                height: tabHeight,
              }}
              aria-label={getAriaLabel('tab', tab.name)}
            >
              <TabContent {...props} item={tab} isActive={tab.key === activeTabKey} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

const TabContent = <T,>({
  item,
  size,
  countVariant = 'blue',
}: {
  item: ITabItem<T>
  isActive: boolean
  size?: ITabsProps<T>['size']
  disabledHover?: ITabsProps<T>['disabledHover']
  badgeActive?: ITabsProps<T>['badgeActive']
  withStopPropagation?: ITabsProps<T>['withStopPropagation']
  handleClickTab?: ITabsProps<T>['handleClickTab']
  handleSelectTab?: ITabsProps<T>['handleSelectTab']
  tabHeight?: ITabsProps<T>['tabHeight']
  countVariant?: ITabsProps<T>['countVariant']
}) => {
  const iconSize = size === 'large' ? 14.7 : 12
  const textSize: TextStylesType = size === 'large' ? 'button-medium' : 'button-small'
  const { name, count, icon, badgeProps, iconProps, statusProps } = item

  const title = (
    <Typography variant={textSize} tag={'div'} className={styles.tab__label} ariaLabel={'label'}>
      {name}
    </Typography>
  )
  const content = () => {
    if (item.tooltipProps) {
      return <Tooltip {...item.tooltipProps}>{title}</Tooltip>
    }

    return <>{title}</>
  }

  return (
    <>
      <div className={styles.content} aria-label={getAriaLabel('tab', 'content')}>
        {icon && (
          <div className={styles.tab__icon} aria-label={getAriaLabel('tab', 'icon')}>
            <Icon icon={icon} fontSize={iconSize} {...iconProps} />
          </div>
        )}

        {content()}

        {count !== undefined && (
          <div className={styles.tab__count} aria-label={getAriaLabel('tab', 'count')}>
            <span className={styles['tab__count--hover']}>
              <Badge content={count} variant={'white'} showAll {...badgeProps} />
            </span>
            <span className={styles['tab__count--hoverActive']}>
              <Badge content={count} variant={countVariant} showAll {...badgeProps} />
            </span>
            <span className={styles['tab__count--default']}>
              <Badge content={count} variant={'grey'} showAll {...badgeProps} />
            </span>
          </div>
        )}
      </div>

      {statusProps && (
        <div className={styles.statusWrap} aria-label={getAriaLabel('tab', 'status')}>
          <Status
            {...statusProps}
            className={classnames(styles.statusWrap__status, statusProps.className)}
          />
        </div>
      )}
    </>
  )
}
